import {
    Autocomplete,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Popper,
    Slide,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AquantCancelIcon from '../resources/images/aq-cancel.svg';
import { ReactComponent as CheckboxIcon } from '../resources/images/checkbox.svg';
import { ReactComponent as CheckedCheckboxIcon } from '../resources/images/checked-checkbox.svg';
import './AddCustomFieldDialog.scss';
import SiDialog from './SiDialog';
import SITooltip from './SITooltip';

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmit: (customFields: string[]) => void;
    fieldsOptions: string[];
    mappingFieldsNames: string[];
    mappedTargets: string[];
};

const Transition = forwardRef(
    (
        props: TransitionProps & {
            children: React.ReactElement;
        },
        ref: React.Ref<unknown>,
    ) => <Slide direction="up" ref={ref} {...props} />,
);

const AddCustomFieldDialog = (props: Props) => {
    const { open, onSubmit, onClose } = props;
    const [customFields, setCustomFields] = useState<string[]>([]);
    const { t } = useTranslation();

    const handleCloseDialog = () => {
        setCustomFields([]);
        onClose();
    };

    const MenuItemComponent = (params, option, { selected }) => {
        let tooltipTitle = '';

        if (props.mappedTargets.includes(option)) {
            tooltipTitle = t('data-settings.custom-field-mapped-option-tooltip');
        } else if (props.mappingFieldsNames.includes(option)) {
            tooltipTitle = t('data-settings.custom-field-existing-mapping');
        }

        return (
            <SITooltip placement="bottom-start" title={tooltipTitle}>
                <span>
                    <li style={tooltipTitle !== '' ? { pointerEvents: 'none', opacity: '0.4' } : {}} {...params}>
                        <Checkbox
                            disableRipple
                            checkedIcon={<CheckedCheckboxIcon />}
                            icon={<CheckboxIcon />}
                            checked={selected}
                            color="primary"
                        />
                        {option}
                    </li>
                </span>
            </SITooltip>
        );
    };

    return (
        <SiDialog open={open} onClose={onClose} TransitionComponent={Transition}>
            <DialogTitle>
                <Stack className="DialogTitle">
                    <Typography className="DialogTitleText">Add a custom field/s</Typography>
                    <img src={AquantCancelIcon} onClick={handleCloseDialog} alt="cancel icon" className="CancelIcon" />
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent classes={{ root: 'DialogContent' }}>
                <Typography
                    className="DialogContentTitle"
                    fontWeight={400}
                    letterSpacing="4%"
                    lineHeight="22.4px"
                    fontSize="16px"
                >
                    Please select custom fields from the list
                </Typography>
                <Typography className="DialogContentRow1">
                    Fields are available on the list only after being added to the template.
                </Typography>
                <Typography className="DialogContentRow2">
                    Note that an added custom field will not appear later on SI.
                </Typography>
                <FormControl size="small" className="FormControlCustomOptions">
                    <Autocomplete
                        multiple
                        PopperComponent={({ style, ...props }) => (
                            <Popper
                                {...props}
                                style={{ ...style, height: 0 }} // width is passed in 'style' prop
                            />
                        )}
                        ListboxProps={{ style: { maxHeight: '230px' } }}
                        renderOption={MenuItemComponent}
                        options={props.fieldsOptions}
                        disableCloseOnSelect
                        onChange={(e, items) => setCustomFields(items)}
                        value={customFields}
                        renderInput={(params) => <TextField {...params} label="Search or select a custom field name" />}
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog} disableRipple className="DialogActionBtn">
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        onSubmit(customFields);
                        setCustomFields([]);
                    }}
                    variant="contained"
                    disableRipple
                    className="DialogActionBtn DialogActionBtnContained"
                >
                    Add
                </Button>
            </DialogActions>
        </SiDialog>
    );
};

export default AddCustomFieldDialog;
